import React from "react";
import { Navbar } from "./Navbar";
import { Hero } from "./Hero";
import { FeatureShowcase } from "./FeatureShowcase";
import { StatsSection } from "./StatsSection";
import { PricingSection } from "./PricingSection";
import { FAQSection } from "./FAQSection";
import { CTABanner } from "./CTABanner";
import { Footer } from "./Footer";
import { MockupPipeline } from "./mockups/MockupPipeline";
import { MockupCalculator } from "./mockups/MockupCalculator";
import { MockupTeamChat } from "./mockups/MockupTeamChat";
import { MockupComments } from "./mockups/MockupComments";

export function LandingPage() {
	return (
		<div className="min-h-screen bg-[hsl(240,6%,6%)] text-white antialiased">
			<Navbar />
			<Hero />

			<div id="features" className="mx-auto max-w-7xl px-6">
				{/* Feature 1: Organization */}
				<FeatureShowcase
					badge="Organization"
					title="Your Comps, Organized"
					description="Save comps to deal decks as you browse. Track every deal across 9 pipeline stages — from first look to sold. Drag, sort, and prioritize with your team in real-time."
					mockup={<MockupPipeline />}
					features={[
						{
							icon: <PipelineIcon />,
							title: "9-Stage Deal Pipeline",
							description:
								"Track deals from discovery to close with color-coded stages",
						},
						{
							icon: <DeckIcon />,
							title: "Custom Deck Organization",
							description:
								"Group comps by market, deal type, or strategy",
						},
						{
							icon: <SourceIcon />,
							title: "Multi-Source Support",
							description:
								"Organize comps from Zillow, Redfin, Realtor.com, and more",
						},
					]}
				/>

				{/* Feature 2: Calculator */}
				<FeatureShowcase
					badge="Analysis"
					title="Know Your Numbers Before You Commit"
					description="Professional ROI calculator built for renovation analysis. Model purchase price, repair costs, holding costs, and financing to project profit before you make an offer."
					mockup={<MockupCalculator />}
					reversed
					features={[
						{
							icon: <BarChartIcon />,
							title: "Before & After Analysis",
							description:
								"Compare as-is value to after-renovation value (ARV)",
						},
						{
							icon: <DollarIcon />,
							title: "Financeable Costs",
							description:
								"Roll repairs, points, and fees into your loan model",
						},
						{
							icon: <LayersIcon />,
							title: "Multiple Scenarios",
							description:
								"Run side-by-side what-if analyses on every deal",
						},
					]}
				/>

				{/* Feature 3: Team */}
				<FeatureShowcase
					badge="Collaboration"
					title="Built for Teams, Not Just Individuals"
					description="Slack-style team messaging built right into your deal flow. Discuss properties, share analysis, and make decisions together — without leaving the tool."
					mockup={<MockupTeamChat />}
					features={[
						{
							icon: <ChatIcon />,
							title: "Channels & DMs",
							description:
								"Dedicated channels per deal, plus private messaging",
						},
						{
							icon: <ShieldIcon />,
							title: "Enterprise Permissions",
							description:
								"8 roles with granular capability controls",
						},
						{
							icon: <ZapIcon />,
							title: "Real-Time Sync",
							description:
								"Changes appear across all team members in under 2 seconds",
						},
					]}
				/>

				{/* Feature 4: Comments / Collaboration */}
				<FeatureShowcase
					badge="Discussion"
					title="Talk Deals, Not Just Data"
					description="Threaded comments on every property. Tag your contractor, discuss rehab scope, react to insights — all in context, right where the data lives. No more scattered texts and emails."
					mockup={<MockupComments />}
					reversed
					features={[
						{
							icon: <ThreadIcon />,
							title: "Threaded Conversations",
							description:
								"Reply in context with full discussion history",
						},
						{
							icon: <MentionIcon />,
							title: "@Mention Anyone",
							description:
								"Tag team members, contractors, and agents directly",
						},
						{
							icon: <ReactionIcon />,
							title: "Reactions & Media",
							description:
								"React with emoji, attach photos and documents",
						},
					]}
				/>

			</div>

			<StatsSection />
			<CTABanner variant="mid" />
			<PricingSection />
			<FAQSection />
			<CTABanner variant="bottom" />
			<Footer />
		</div>
	);
}

/* ─── Inline SVG Icons ────────────────────────────────────────────── */

function PipelineIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
		</svg>
	);
}

function DeckIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122" />
		</svg>
	);
}

function SourceIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.556a4.5 4.5 0 00-6.364-6.364L4.5 8.25" />
		</svg>
	);
}

function BarChartIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
		</svg>
	);
}

function DollarIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
		</svg>
	);
}

function LayersIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" />
		</svg>
	);
}

function ChatIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
		</svg>
	);
}

function ShieldIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
		</svg>
	);
}

function ZapIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
		</svg>
	);
}

function ThreadIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
		</svg>
	);
}

function MentionIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 10-2.636 6.364M16.5 12V8.25" />
		</svg>
	);
}

function ReactionIcon() {
	return (
		<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
		</svg>
	);
}

